import { useEffect, useRef, useCallback } from 'react'

/**
 * Hook to detect device shake gesture
 * @param {Function} onShake - Callback when shake is detected
 * @param {Object} options - Configuration options
 */
export function useShakeDetector(onShake, options = {}) {
  const {
    threshold = 15,        // Acceleration threshold to trigger shake
    timeout = 1000,        // Cooldown between shakes (ms)
    enabled = true
  } = options

  const lastShake = useRef(0)
  const lastX = useRef(null)
  const lastY = useRef(null)
  const lastZ = useRef(null)

  const handleMotion = useCallback((event) => {
    if (!enabled) return

    const { accelerationIncludingGravity } = event
    if (!accelerationIncludingGravity) return

    const { x, y, z } = accelerationIncludingGravity

    if (lastX.current !== null) {
      const deltaX = Math.abs(x - lastX.current)
      const deltaY = Math.abs(y - lastY.current)
      const deltaZ = Math.abs(z - lastZ.current)

      // Check if movement exceeds threshold
      if ((deltaX > threshold && deltaY > threshold) || 
          (deltaX > threshold && deltaZ > threshold) || 
          (deltaY > threshold && deltaZ > threshold)) {
        
        const now = Date.now()
        if (now - lastShake.current > timeout) {
          lastShake.current = now
          onShake?.()
        }
      }
    }

    lastX.current = x
    lastY.current = y
    lastZ.current = z
  }, [onShake, threshold, timeout, enabled])

  useEffect(() => {
    if (!enabled) return
    
    // Check if DeviceMotionEvent is available
    if (typeof DeviceMotionEvent === 'undefined') {
      console.log('DeviceMotionEvent not supported')
      return
    }

    // iOS 13+ requires permission
    if (typeof DeviceMotionEvent.requestPermission === 'function') {
      // We'll need user interaction to request permission
      // This is handled in the component that uses this hook
    } else {
      window.addEventListener('devicemotion', handleMotion)
    }

    return () => {
      window.removeEventListener('devicemotion', handleMotion)
    }
  }, [handleMotion, enabled])

  // Function to request permission on iOS
  const requestPermission = useCallback(async () => {
    if (typeof DeviceMotionEvent.requestPermission === 'function') {
      try {
        const permission = await DeviceMotionEvent.requestPermission()
        if (permission === 'granted') {
          window.addEventListener('devicemotion', handleMotion)
          return true
        }
      } catch (e) {
        console.error('Error requesting motion permission:', e)
      }
      return false
    }
    return true // Permission not needed
  }, [handleMotion])

  return { requestPermission }
}

/**
 * Check if shake detection is available
 */
export function supportsShakeDetection() {
  return typeof DeviceMotionEvent !== 'undefined'
}
